import { React, useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { useNavigate } from "react-router-dom";
import {
  BlockStack,
  Button,
  IndexTable,
  LegacyCard,
  Page,
  Pagination,
  Spinner,
  TextField,
  Text,
} from "@shopify/polaris";
import {
  SearchIcon,
  LocationIcon,
  ListBulletedIcon,
} from "@shopify/polaris-icons";
import { UserAuth } from "../../context/AuthContext";
import { useProviderLive } from "../../utility/useProviderLive";
import ClabeBadge from "./ClabeBadge";
import ProviderMap from "./ProviderMap";

function AllServiceProviders() {
  const navigate = useNavigate();
  const { hasPermission } = UserAuth();

  const [providers, setProviders] = useState([]);

  // Badges update themselves as verifications land — during a bulk backfill
  // that is the whole list changing over several minutes.
  useProviderLive((row) => {
    setProviders((prev) =>
      prev.map((p) =>
        String(p.id) === String(row.id)
          ? { ...p, clabe_status: row.clabe_status, bank_holder_name: row.bank_holder_name }
          : p
      )
    );
  });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // List <-> map view toggle. All locations are fetched lazily the first time
  // the map is opened (the list itself is paginated, so it can't feed the map).
  const [view, setView] = useState("list");
  const [locations, setLocations] = useState([]);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(false);

  const pageSize = 20;

  const fetchAllLocations = async () => {
    setLocationsLoading(true);
    const { data, error } = await supabase
      .from("provider_locations")
      .select(
        "id, label, street_1, street_2, city, state, latitude, longitude, provider_id, service_providers(name)"
      )
      .order("provider_id", { ascending: true });
    if (error) {
      console.error(error.message);
      setLocationsLoading(false);
      return;
    }
    setLocations(
      (data || []).map((r) => ({
        ...r,
        provider_name: r.service_providers?.name?.trim() || "Proveedor",
      }))
    );
    setLocationsLoaded(true);
    setLocationsLoading(false);
  };

  const toggleView = () => {
    const next = view === "list" ? "map" : "list";
    if (next === "map" && !locationsLoaded) fetchAllLocations();
    setView(next);
  };

  const fetchProviders = async () => {
    setLoading(true);
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from("service_providers")
      .select("id, name, rfc, contact_name, city, state, clabe_status, bank_holder_name")
      .order("name", { ascending: true });

    const s = search.trim();
    if (s) {
      query = query.or(
        `name.ilike.%${s}%,rfc.ilike.%${s}%,contact_name.ilike.%${s}%,city.ilike.%${s}%,state.ilike.%${s}%`
      );
    }

    const { data, error } = await query.range(from, to);
    if (error) {
      console.error(error.message);
      setLoading(false);
      return;
    }

    setProviders(data || []);
    setHasMore((data?.length ?? 0) >= pageSize);
    setLoading(false);
  };

  useEffect(() => {
    fetchProviders();
  }, [page]);

  const rowMarkup = providers.map((provider, index) => (
    <IndexTable.Row
      id={String(provider.id)}
      key={provider.id}
      position={index}
      onClick={() => navigate(`/service_providers/${provider.id}`)}
    >
      <IndexTable.Cell>{provider.id}</IndexTable.Cell>
      <IndexTable.Cell>
        <Text fontWeight="semibold">{provider.name}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{provider.rfc || "-"}</IndexTable.Cell>
      <IndexTable.Cell>{provider.contact_name || "-"}</IndexTable.Cell>
      <IndexTable.Cell>
        {[provider.city, provider.state].filter(Boolean).join(", ") || "-"}
      </IndexTable.Cell>
      <IndexTable.Cell>
        {/* Only badge what needs attention. Once the fleet is verified a badge
            on every row is uniform noise — the exceptions are the signal. */}
        {provider.clabe_status === "verified" ? null : (
          <ClabeBadge
            status={provider.clabe_status}
            holderName={provider.bank_holder_name}
          />
        )}
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <AppWrapper>
      <Page
        title="Proveedores de Servicios"
        primaryAction={
          hasPermission("service.provider.create")
            ? { content: "Crear", url: "/service_providers/new" }
            : undefined
        }
        secondaryActions={[
          {
            content: view === "list" ? "Ver mapa" : "Ver lista",
            icon: view === "list" ? LocationIcon : ListBulletedIcon,
            onAction: toggleView,
          },
        ]}
      >
        {view === "map" ? (
          locationsLoading && !locationsLoaded ? (
            <Spinner size="small" />
          ) : (
            <ProviderMap locations={locations} />
          )
        ) : (
        <BlockStack gap={400}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              fetchProviders();
            }}
          >
            <div style={{ display: "flex", gap: "0.25rem" }}>
              <span style={{ flexGrow: 1 }}>
                <TextField
                  autoComplete="off"
                  placeholder="Buscar por razón social, RFC, contacto o ciudad"
                  value={search}
                  onChange={(value) => setSearch(value)}
                  disabled={loading}
                />
              </span>
              <span
                style={{
                  aspectRatio: 1,
                  display: "flex",
                  alignItems: "center",
                  width: 32,
                  height: 32,
                }}
              >
                <Button icon={SearchIcon} submit disabled={loading} />
              </span>
            </div>
          </form>
          {loading ? (
            <Spinner size="small" />
          ) : (
            <LegacyCard>
              <>
                <IndexTable
                  resourceName={{
                    singular: "Proveedor",
                    plural: "Proveedores",
                  }}
                  selectable={false}
                  itemCount={providers?.length ?? 0}
                  headings={[
                    { title: "ID" },
                    { title: "Razón Social" },
                    { title: "RFC" },
                    { title: "Contacto" },
                    { title: "Ubicación" },
                    { title: "Cuenta" },
                  ]}
                >
                  {rowMarkup}
                </IndexTable>
                <div
                  style={{
                    padding: "1rem",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => setPage(page - 1)}
                    hasNext={hasMore}
                    onNext={() => setPage(page + 1)}
                  />
                </div>
              </>
            </LegacyCard>
          )}
        </BlockStack>
        )}
      </Page>
    </AppWrapper>
  );
}

export default AllServiceProviders;
